import { useMemo, useState, type FormEvent } from 'react';
import { useAppData } from '../app/AppProvider';
import { navigate } from '../app/router';
import { EmptyState } from '../components/EmptyState';
import { StationAutocomplete } from '../components/StationAutocomplete';
import { TagInput } from '../components/TagInput';
import { TopBar } from '../components/TopBar';
import { METRO_STATION_BY_ID, METRO_STATIONS } from '../data/metro';
import { INITIAL_TAGS, RETURN_INTENT_LABELS } from '../domain/labels';
import { findDuplicatePlace, hasPlaceValidationErrors, validatePlaceInput } from '../domain/place';
import type { PlaceInput, PlaceStatus, PlaceValidationErrors, ReturnIntent } from '../domain/types';

interface PlaceFormPageProps {
  placeId?: string;
  initialStationId?: string | null;
}

export function PlaceFormPage({ placeId, initialStationId = null }: PlaceFormPageProps) {
  const { data, createPlace, updatePlace } = useAppData();
  const existing = placeId ? data.places.find((place) => place.id === placeId) : undefined;
  const editingMissing = Boolean(placeId && !existing);
  const validInitialStation = initialStationId && METRO_STATION_BY_ID.has(initialStationId) ? initialStationId : null;

  const [yandexMapsUrl, setYandexMapsUrl] = useState(existing?.yandexMapsUrl ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [stationId, setStationId] = useState<string | null>(existing?.stationId ?? validInitialStation);
  const [status, setStatus] = useState<PlaceStatus>(existing?.status ?? 'want');
  const [returnIntent, setReturnIntent] = useState<ReturnIntent>(existing?.returnIntent ?? null);
  const [tags, setTags] = useState<string[]>(existing?.tags ?? []);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const [errors, setErrors] = useState<PlaceValidationErrors>({});
  const [duplicateName, setDuplicateName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const clearError = (field: keyof PlaceValidationErrors) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const knownTags = useMemo(() => {
    const all = [...INITIAL_TAGS, ...data.customTags, ...data.places.flatMap((place) => place.tags)];
    const unique = new Map<string, string>();
    all.forEach((tag) => unique.set(tag.toLocaleLowerCase('ru'), tag));
    return [...unique.values()].sort((left, right) => left.localeCompare(right, 'ru', { sensitivity: 'base' }));
  }, [data.customTags, data.places]);

  if (editingMissing) {
    return (
      <main className="page">
        <TopBar title="Место не найдено" />
        <EmptyState title="Редактировать нечего">Место было удалено или отсутствует в текущих данных.</EmptyState>
      </main>
    );
  }

  const backHref = existing
    ? `#/place/${encodeURIComponent(existing.id)}`
    : stationId
      ? `#/station/${encodeURIComponent(stationId)}`
      : '#/';

  const buildInput = (): PlaceInput => ({
    yandexMapsUrl,
    name,
    stationId: stationId ?? '',
    status,
    returnIntent,
    tags,
    comment,
  });

  const persist = async (acceptDuplicate: boolean) => {
    const input = buildInput();
    const nextErrors = validatePlaceInput(input, METRO_STATIONS);
    setErrors(nextErrors);
    setSaveError(null);
    if (hasPlaceValidationErrors(nextErrors)) return;

    const duplicate = findDuplicatePlace(data.places, input.yandexMapsUrl, existing?.id);
    if (duplicate && !acceptDuplicate) {
      setDuplicateName(duplicate.name);
      return;
    }

    setDuplicateName(null);
    setSaving(true);
    try {
      const saved = existing
        ? await updatePlace(existing.id, input)
        : await createPlace(input);
      navigate(`#/place/${encodeURIComponent(saved.id)}`, true);
    } catch (cause) {
      console.error(cause);
      setSaveError('Не удалось сохранить место. Данные не потеряны — попробуйте ещё раз.');
      setSaving(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void persist(false);
  };

  return (
    <main className="page">
      <TopBar title={existing ? 'Редактирование' : 'Новое место'} backHref={backHref} />
      <form className="form" noValidate onSubmit={submit}>
        <div className="form-card form">
          <label className="field">
            <span className="field-label required">Ссылка на Яндекс Карты</span>
            <input
              className="field-input"
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              value={yandexMapsUrl}
              placeholder="https://yandex.ru/maps/…"
              aria-invalid={Boolean(errors.yandexMapsUrl)}
              onChange={(event) => { setYandexMapsUrl(event.target.value); setDuplicateName(null); clearError('yandexMapsUrl'); }}
            />
            {errors.yandexMapsUrl ? <span className="field-error">{errors.yandexMapsUrl}</span> : null}
          </label>

          {duplicateName ? (
            <div className="field-warning" role="alert">
              Такая ссылка уже есть у места «{duplicateName}». Это может быть дубликат.
              <button className="button button--quiet button--full" type="button" disabled={saving} onClick={() => void persist(true)}>Всё равно сохранить</button>
            </div>
          ) : null}

          <label className="field">
            <span className="field-label required">Название</span>
            <input className="field-input" value={name} placeholder="Название места" aria-invalid={Boolean(errors.name)} onChange={(event) => { setName(event.target.value); clearError('name'); }} />
            {errors.name ? <span className="field-error">{errors.name}</span> : null}
          </label>

          <div className="field">
            <span className="field-label required">Станция метро</span>
            <StationAutocomplete stations={METRO_STATIONS} value={stationId} onChange={(nextStationId) => { setStationId(nextStationId); if (nextStationId) clearError('stationId'); }} error={errors.stationId} />
          </div>

          <fieldset className="fieldset">
            <legend className="fieldset-legend required">Статус</legend>
            <div className="segmented">
              <button className="segmented__button" type="button" aria-pressed={status === 'want'} onClick={() => { setStatus('want'); setReturnIntent(null); clearError('status'); clearError('returnIntent'); }}>Хочу посетить</button>
              <button className="segmented__button" type="button" aria-pressed={status === 'visited'} onClick={() => { setStatus('visited'); clearError('status'); }}>Был</button>
            </div>
            {errors.status ? <p className="field-error">{errors.status}</p> : null}
          </fieldset>

          {status === 'visited' ? (
            <label className="field">
              <span className="field-label">Хочу ли вернуться</span>
              <select className="field-select" value={returnIntent ?? ''} onChange={(event) => { setReturnIntent((event.target.value || null) as ReturnIntent); clearError('returnIntent'); }}>
                <option value="">Не указано</option>
                <option value="yes">{RETURN_INTENT_LABELS.yes}</option>
                <option value="no">{RETURN_INTENT_LABELS.no}</option>
                <option value="unsure">{RETURN_INTENT_LABELS.unsure}</option>
              </select>
              {errors.returnIntent ? <span className="field-error">{errors.returnIntent}</span> : null}
            </label>
          ) : null}

          <div className="field">
            <span className="field-label">Теги</span>
            <TagInput value={tags} knownTags={knownTags} onChange={setTags} />
          </div>

          <label className="field">
            <span className="field-label">Комментарий</span>
            <textarea className="field-textarea" value={comment} placeholder="Что важно запомнить об этом месте" onChange={(event) => setComment(event.target.value)} />
          </label>
        </div>

        {saveError ? <p className="notice notice--danger" role="alert">{saveError}</p> : null}
        <div className="form-actions">
          <button className="button button--full" type="submit" disabled={saving}>{saving ? 'Сохранение…' : existing ? 'Сохранить изменения' : 'Добавить место'}</button>
          <a className="button button--secondary button--full" href={backHref}>Отмена</a>
        </div>
      </form>
    </main>
  );
}
